/**
 * Shared type vocabulary for Atlassian Guard redaction and blocking UI.
 */

/** Surface that produced the block. Drives body copy in `BlockEnforcementMessage`. */
export type BlockType = 'PROMPT_BLOCKED' | 'RESPONSE_BLOCKED' | 'ATTACHMENT_BLOCKED';

/**
 * Visual background variant for the scribble bar.
 *
 * - `'dark'`  — animated SVG scribble for dark surfaces.
 * - `'light'` — static dark bar for light surfaces.
 */
export type RedactionBackground = 'dark' | 'light';

/** Product surface rendering the enforcement message. */
export type BlockContext = 'rovo-chat' | 'confluence-page' | 'confluence-comment';

export const REDACTION_EXTENSION_TYPE = 'com.atlassian.guard-premium' as const;

export const REDACTION_EXTENSION_KEY = 'redaction' as const;

/** Surface where a redaction happened; drives the flag description's location. */
export type GuardFlagSurface =
	| 'page-body'
	| 'page-title'
	| 'comment'
	| 'issue-description'
	| 'issue-summary'
	| 'custom-field';

/** Token the backend substitutes into text/ADF for redacted content. */
export const REDACTION_TOKENS = ['[REDACTED]', '[REDACT]'] as const;
export type RedactionToken = (typeof REDACTION_TOKENS)[number];

/**
 * Unicode "Full Block" (U+2588). Reactive Guard Detect and Confluence titles
 * represent plain-text redactions as a run of these block characters rather
 * than a bracket token. A run of one or more is treated as a single redaction.
 */
export const REDACTION_BLOCK_CHAR = '\u2588';

/**
 * Single source of truth for every plain-text redaction form `RedactedContent`
 * recognizes. Each entry is a regex source fragment (already escaped where it
 * needs to be) describing one form. Both the split pattern and the per-part
 * test pattern are derived from this list, so adding a new form here keeps the
 * splitter and the classifier in lockstep (no drift).
 */
export const REDACTION_MATCHER_SOURCES: readonly string[] = [
	// Bracket tokens, derived from REDACTION_TOKENS so the literals stay in one place.
	...REDACTION_TOKENS.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
	// A run of one or more Unicode full-block characters.
	`${REDACTION_BLOCK_CHAR}+`,
];
